import { useId } from 'react';
import type { ReactNode } from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { BadgeProps } from 'react-bootstrap';

type TooltipPlacement = 'top' | 'right' | 'bottom' | 'left';

interface HelpTooltipProps {
  description: ReactNode;
  label?: string;
  placement?: TooltipPlacement;
  className?: string;
}

const readableLabel = (value: ReactNode, fallback: string) => {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  return fallback;
};

const tooltipId = (label: string, unique: string) =>
  `agent-help-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')}-${unique.replace(/[^a-z0-9]+/gi, '')}`;

const HelpTooltip = ({ description, label = 'Help', placement = 'top', className = '' }: HelpTooltipProps) => {
  const unique = useId();

  return (
    <OverlayTrigger placement={placement} overlay={<Tooltip id={tooltipId(label, unique)}>{description}</Tooltip>}>
      <span
        role="button"
        tabIndex={0}
        aria-label={label}
        className={`d-inline-flex align-items-center justify-content-center rounded-circle border text-muted ${className}`}
        style={{ width: 16, height: 16, fontSize: 11, cursor: 'help', lineHeight: 1 }}
      >
        ?
      </span>
    </OverlayTrigger>
  );
};

interface HelpLabelProps {
  children: ReactNode;
  description: ReactNode;
  className?: string;
  placement?: TooltipPlacement;
}

export const HelpLabel = ({ children, description, className = '', placement = 'top' }: HelpLabelProps) => (
  <span className={`d-inline-flex align-items-center gap-1 ${className}`}>
    <span>{children}</span>
    <HelpTooltip description={description} label={String(children)} placement={placement} />
  </span>
);

interface HelpBadgeProps {
  children: ReactNode;
  description: ReactNode;
  bg?: string;
  text?: string;
  className?: string;
  placement?: TooltipPlacement;
}

export const HelpBadge = ({
  children,
  description,
  bg = 'secondary',
  text,
  className = '',
  placement = 'top',
}: HelpBadgeProps) => {
  const unique = useId();
  const label = readableLabel(children, 'Badge help');
  const badgeProps: BadgeProps = { bg, className };
  if (text !== undefined) {
    badgeProps.text = text;
  }

  return (
    <OverlayTrigger placement={placement} overlay={<Tooltip id={tooltipId(label, unique)}>{description}</Tooltip>}>
      <span role="button" tabIndex={0} aria-label={label} className="d-inline-flex" style={{ cursor: 'help' }}>
        <Badge {...badgeProps}>{children}</Badge>
      </span>
    </OverlayTrigger>
  );
};

export default HelpTooltip;
